import type { LayerKey } from '@festina-life/api'
import {
  type ConfigurableServer,
  notEmpty,
  type Optional,
} from '@festina-life/core'
import { type PathPart, Typography } from '@festina-life/ui/structure'
import type { ReactNode } from 'react'

import {
  isFunctionPathDeclaration,
  splitPartialTemplatePath,
} from '../../context/pathHelpers'
import { configurationItem } from '../../system/paths'

type BuildPartsParams = {
  value: string
  templateId: string
  server: ConfigurableServer
  layer: LayerKey
  block: string
  branch: Optional<string>
  workspaceName: Optional<string>
  itemType: 'template' | 'library'
  currentPath?: string
  isInteractive: boolean
}

/**
 * Recursively builds PathPart array from a partial template path.
 * Groups indices with their parent field names as a single breadcrumb part.
 */
export function buildPartialTemplatePathParts({
  value,
  templateId,
  server,
  layer,
  block,
  branch,
  workspaceName,
  itemType,
  currentPath = '',
  isInteractive,
}: BuildPartsParams): PathPart[] {
  if (isFunctionPathDeclaration(value)) {
    const functionName = value.substring('.[function]'.length)
    const pathWithFunction = currentPath + value

    if (isInteractive) {
      return [
        {
          label: functionName,
          href: configurationItem({
            server,
            block,
            layer,
            branch,
            workspaceName,
            itemId: templateId,
            type: itemType,
            path: pathWithFunction,
          }),
        },
      ]
    } else {
      return [
        {
          label: functionName,
        },
      ]
    }
  }

  const {
    field: { fieldName, subTemplateId, index },
    remainingPath,
  } = splitPartialTemplatePath(value)

  if (!fieldName) {
    return []
  }

  // Build the current path segment
  const indexPart = notEmpty(index) ? `[${index}]` : ''
  const subTemplatePart = notEmpty(subTemplateId) ? `{${subTemplateId}}` : ''
  const pathSegment = `.${fieldName}${subTemplatePart}${indexPart}`
  const newCurrentPath = currentPath + pathSegment

  // Build the label with field name, index, and subTemplateId
  const labelComponents: ReactNode[] = [
    <Typography key="field">{fieldName}</Typography>,
  ]

  if (notEmpty(subTemplateId)) {
    labelComponents.push(
      <Typography key="subTemplate" size="sm" intensity="secondary">
        {subTemplateId}
      </Typography>,
    )
  }
  if (notEmpty(index)) {
    labelComponents.push(
      <Typography key="index" size="sm" intensity="tertiary">
        [{index}]
      </Typography>,
    )
  }

  const label = (
    <div className="flex items-center gap-x-1">{labelComponents}</div>
  )

  const parts: PathPart[] = []

  // Create a single breadcrumb part with the combined label
  parts.push({
    label,
    ...(isInteractive
      ? {
          href: configurationItem({
            server,
            layer,
            block,
            branch,
            workspaceName,
            itemId: templateId,
            type: itemType,
            path: newCurrentPath,
          }),
        }
      : {}),
  })

  // Recursively process remaining path
  if (remainingPath) {
    const remainingParts = buildPartialTemplatePathParts({
      value: remainingPath,
      templateId,
      server,
      block,
      layer,
      branch,
      workspaceName,
      itemType,
      currentPath: newCurrentPath,
      isInteractive,
    })
    parts.push(...remainingParts)
  }

  return parts
}
